/** @file The resolved view of a camera under a profile: basis, z scale, world grid, rounded target, origin. */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import { orthoBasis, type OrthoBasis } from '@zilo/pose3d-math/project/orthoBasis';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { Camera } from '@zilo/pose3d-render-core/contract';
import { zScaleViewOf } from '@zilo/pose3d-render-core/camera/zScaleViewOf';
import { roundWorld } from '@zilo/pose3d-render-core/place/roundWorld';
import { worldGridOf } from '@zilo/pose3d-render-core/place/worldGridOf';

/**
 * What every projection helper reads; one member per camera kind (15.11). `grid` is the profile's world rounding grid
 * (21.4); `target` is on it when `snap`; `originX/Y` is the viewport centre floored (no lattice shift, R3);
 * `viewport` is the camera's `[w, h]` (the two-point metric of 21.6 reads it).
 */
export type View = {
  kind: 'ortho'; basis: OrthoBasis; pixelsPerUnit: number; zScaleView: number; yawDeg: number; pitchDeg: number;
  grid: Vec3; snap: boolean; target: Vec3; originX: number; originY: number; viewport: [number, number];
};

/** One builder per camera kind; a new kind is a type error until it has an entry (15.11). */
const BY_KIND: { [K in Camera['kind']]: (camera: Extract<Camera, { kind: K }>, profile: Profile) => View } = {
  ortho: (camera, profile) => {
    const grid = worldGridOf(profile);
    const snap = profile.snap === 'pixel';
    return {
      kind: 'ortho', basis: orthoBasis(camera.yawDeg, camera.pitchDeg), pixelsPerUnit: camera.pixelsPerUnit,
      zScaleView: zScaleViewOf(profile), yawDeg: camera.yawDeg, pitchDeg: camera.pitchDeg, grid, snap,
      target: snap ? roundWorld(camera.target, grid) : camera.target,
      originX: Math.floor(camera.viewport[0] / 2), originY: Math.floor(camera.viewport[1] / 2), viewport: camera.viewport,
    };
  },
};

/** The view of `camera` under `profile`. */
export function viewOf(camera: Camera, profile: Profile): View {
  return BY_KIND[camera.kind](camera, profile);
}
